import { defineAsyncComponent, Component, ref, onMounted, type Ref } from 'vue'

// Pre-cargar todos los componentes disponibles usando import.meta.glob (genérico)
const brandModules = import.meta.glob<any>('@brand/**/*.vue')
const coreModules = import.meta.glob<any>('/src/**/*.vue')
const brandModulesTs = import.meta.glob<any>('@brand/**/*.ts')
const coreModulesTs = import.meta.glob<any>('/src/**/*.ts')

// Normalizar las keys de los módulos para facilitar el lookup
const brandComponents: Record<string, () => Promise<any>> = {}
const coreComponents: Record<string, () => Promise<any>> = {}
const brandModulesMap: Record<string, () => Promise<any>> = {}
const coreModulesMap: Record<string, () => Promise<any>> = {}

// Normalizar a un solo formato (/) para que el lookup funcione en Windows y Mac
const toNormalizedKey = (p: string) => p.replace(/\\/g, '/')

// Normalizar paths de brand a key relativa (ej: @brand/foo/bar.ts o /path/brand/src/foo/bar.ts → foo/bar.ts)
function stripBrandPrefix(p: string): string {
  const n = toNormalizedKey(p)
  const withoutAlias = n.replace(/^@brand\/?/, '')
  if (withoutAlias !== n) return withoutAlias
  const srcIndex = n.indexOf('brand/src/')
  if (srcIndex !== -1) return n.slice(srcIndex + 'brand/src/'.length)
  return n
}
Object.keys(brandModules).forEach(path => {
  const normalizedPath = stripBrandPrefix(path)
  brandComponents[normalizedPath] = brandModules[path]
})
Object.keys(brandModulesTs).forEach(path => {
  const normalizedPath = stripBrandPrefix(path)
  brandModulesMap[normalizedPath] = brandModulesTs[path]
})

// Normalizar paths de core: /src/... o src/... → ...
const normalizeCorePath = (p: string) => toNormalizedKey(p.replace(/^\/?src\//, ''))
Object.keys(coreModules).forEach(path => {
  const normalizedPath = normalizeCorePath(path)
  coreComponents[normalizedPath] = coreModules[path]
})
Object.keys(coreModulesTs).forEach(path => {
  const normalizedPath = normalizeCorePath(path)
  coreModulesMap[normalizedPath] = coreModulesTs[path]
})

/**
 * Composable para cargar componentes con fallback brand → core
 * 
 * Orden de carga:
 * 1. Intenta cargar de brand/{componentPath}
 * 2. Si no existe, carga de core/{componentPath}
 * 3. Si ninguno existe, lanza error
 * 
 * @param componentPath - Ruta relativa del componente (ej: 'components/common/Button.vue')
 * @returns Component - Componente asíncrono listo para usar
 * 
 * @example
 * const MyButton = useBrandComponent('components/common/Button.vue')
 * const DataTable = useBrandComponent('components/modules/DataTable.vue')
 */
export function useBrandComponent(componentPath: string): Component {
  return defineAsyncComponent(async () => {
    const key = toNormalizedKey(componentPath)
    // Intentar cargar de brand primero
    if (brandComponents[key]) {
      try {
        const brandComponent = await brandComponents[key]()
        console.log(`✅ [Brand] Component loaded: ${componentPath}`)
        return brandComponent.default || brandComponent
      } catch (brandError) {
        console.warn(`⚠️ [Brand] Component found but failed to load: ${componentPath}`, brandError)
      }
    }
    
    // Fallback a core
    if (coreComponents[key]) {
      try {
        const coreComponent = await coreComponents[key]()
        console.log(`ℹ️ [Core] Component loaded: ${componentPath}`)
        return coreComponent.default || coreComponent
      } catch (coreError) {
        console.error(`❌ [Core] Component found but failed to load: ${componentPath}`, coreError)
        throw coreError
      }
    }
    
    // Si no existe en ninguno
    console.error(`❌ Component not found in brand or core: ${componentPath}`)
    console.info('Tried to load:', componentPath)
    console.info('Available brand components:', Object.keys(brandComponents).slice(0, 5))
    console.info('Available core components (sample):', Object.keys(coreComponents).slice(0, 5), '...')
    throw new Error(`Component not found: ${componentPath}`)
  })
}

/**
 * Variante para cargar múltiples componentes a la vez
 * 
 * @example
 * const { Button, Input, Modal } = useBrandComponents({
 *   Button: 'components/common/Button.vue',
 *   Input: 'components/common/Input.vue',
 *   Modal: 'components/common/Modal.vue'
 * })
 */
export function useBrandComponents<T extends Record<string, string>>(
  components: T
): Record<keyof T, Component> {
  const result = {} as Record<keyof T, Component>
  
  for (const [key, path] of Object.entries(components)) {
    result[key as keyof T] = useBrandComponent(path)
  }
  
  return result
}


export async function loadBrandModule<T = Record<string, unknown>>(
  modulePath: string
): Promise<T> {
  const key = toNormalizedKey(modulePath)

  // 1. Brand
  if (brandModulesMap[key]) {
    try {
      const mod = await brandModulesMap[key]()
      return mod as T
    } catch (e) {
      console.warn(`⚠️ [Brand] Module failed to load: ${modulePath}`, e)
    }
  }

  // 2. Core
  if (coreModulesMap[key]) {
    try {
      const mod = await coreModulesMap[key]()
      return mod as T
    } catch (e) {
      console.error(`❌ [Core] Module failed to load: ${modulePath}`, e)
      throw e
    }
  }

  throw new Error(`Module not found: ${modulePath}`)
}

export function useBrandModule<T = Record<string, unknown>>(
  modulePath: string
): Ref<T | null> {
  const moduleRef = ref<T | null>(null) as Ref<T | null>
  onMounted(() => {
    loadBrandModule<T>(modulePath).then((mod) => {
      moduleRef.value = mod
    })
  })
  return moduleRef
}
